import axios from "axios";
import store from "@/store";
import router from "@/router";
import { deduplicateRequest } from "@/utils/requestOptimizer";
import errorService from "@/services/ErrorService";

// Constants
const localBaseUrl = `http://${window.location.hostname}:5001`;
const API_BASE_URL = process.env.NODE_ENV === "local" ? localBaseUrl : process.env.VUE_APP_API_BASE_URL;
const JWT_ERROR_MESSAGES = [
  "jwt must be provided",
  "invalid signature",
  "Invalid request. Token is not in store"
];

const JWT_EXPIRED_MESSAGES = [
  "jwt expired"
];

// Token refresh state management
let isRefreshing = false;
let failedQueue = [];
let tokenRefreshAttempts = 0;
let lastRefreshFailTime = 0;

// Phase 1C: permission-context refresh-on-403 state
let permRefreshPromise = null; // shared in-flight loadPermissionContext (dedupes 403 bursts)
let lastClinicRedirectAt = 0; // debounce redirect-to-/clinic so a 403 burst pushes once
const CLINIC_REDIRECT_COOLDOWN = 5000;
const PERMISSION_CONTEXT_URL = "/api/auth/context"; // excluded from refresh-retry to avoid recursion

// Constants for retry logic
const MAX_REFRESH_ATTEMPTS = 3;
const REFRESH_COOLDOWN = 30000; // 30 seconds

// Proactive token refresh
let refreshTimerId = null;
const REFRESH_BUFFER = 30; // seconds before expiry to trigger refresh

const getTokenExpiry = (token) => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = payload.length % 4;
    if (pad) payload += '='.repeat(4 - pad);
    const decoded = JSON.parse(atob(payload));
    return typeof decoded.exp === 'number' ? decoded.exp : null;
  } catch {
    return null;
  }
};

const cancelProactiveRefresh = () => {
  if (refreshTimerId) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }
};

const doProactiveRefresh = async () => {
  if (isRefreshing) return;
  if (!localStorage.getItem("access_token")) return;
  if (Date.now() - lastRefreshFailTime < REFRESH_COOLDOWN) return;
  if (tokenRefreshAttempts >= MAX_REFRESH_ATTEMPTS) return;

  isRefreshing = true;
  tokenRefreshAttempts++;
  try {
    await store.dispatch("moduleAuth/refreshToken");
    processQueue(null);
    tokenRefreshAttempts = 0;
    scheduleProactiveRefresh();
  } catch (error) {
    lastRefreshFailTime = Date.now();
    processQueue(error);
  } finally {
    isRefreshing = false;
  }
};

const scheduleProactiveRefresh = () => {
  cancelProactiveRefresh();
  const token = localStorage.getItem("access_token");
  if (!token) return;
  const exp = getTokenExpiry(token);
  if (!exp) return;

  const now = Math.floor(Date.now() / 1000);
  const delay = (exp - REFRESH_BUFFER) - now;

  if (delay <= 0) {
    if (!isRefreshing) doProactiveRefresh();
    return;
  }

  // Cap at 30 min to handle very long-lived tokens reliably
  refreshTimerId = setTimeout(() => {
    refreshTimerId = null;
    doProactiveRefresh();
  }, Math.min(delay, 30 * 60) * 1000);
};

const processQueue = (error) => {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      // Don't retry if refresh failed
      reject(error);
    } else {
      // Only retry if refresh succeeded
      resolve(axios(config));
    }
  });
  failedQueue = [];
  
  // Reset refresh attempts on successful refresh
  if (!error) {
    tokenRefreshAttempts = 0;
  }
};

const redirectToLogin = () => {
  cancelProactiveRefresh();
  // A forced logout never runs moduleUser/logout; drop the cached AI Assistant
  // clinics so the next user in this tab cannot see them.
  store.dispatch("moduleMcpSetting/resetMe");
  const currentPath = window.location.pathname;
  if (currentPath !== "/login" && currentPath !== "/oauth2") {
    router.push({ path: "/login", query: { from: currentPath } }).catch(navErr => {
      if (navErr.name !== 'NavigationDuplicated') {
        console.error(navErr);
      }
    });
  }
};

// Phase 1C: a 403 that survives a permission-context refresh means the user
// genuinely lacks access — bounce them back to the clinic chooser. Debounced so
// a burst of 403s fires a single navigation.
const redirectToClinic = () => {
  const now = Date.now();
  if (now - lastClinicRedirectAt < CLINIC_REDIRECT_COOLDOWN) return;
  lastClinicRedirectAt = now;
  const currentPath = window.location.pathname;
  if (currentPath !== "/clinic" && currentPath !== "/login" && currentPath !== "/oauth2") {
    errorService.showErrorToast(
      "warning",
      "คุณไม่มีสิทธิ์เข้าถึงหน้านี้ กำลังกลับสู่หน้าเลือกคลินิก"
    );
    router.push({ path: "/clinic" }).catch(navErr => {
      if (navErr.name !== "NavigationDuplicated") {
        console.error(navErr);
      }
    });
  }
};

// Axios configuration
const configureAxios = () => {
  axios.defaults.baseURL = API_BASE_URL;
  axios.defaults.withCredentials = true;
  // Remove unnecessary headers that trigger CORS preflight
  // Content-Type will be set automatically for JSON requests
  // Accept-Language is rarely needed and triggers preflight

  // Request interceptor
  axios.interceptors.request.use(
    req => {
      // Skip authentication and modification for S3 requests
      const isS3Request = /\.s3[.-]/.test(req.url) || req.skipAuthInterceptor;
      
      if (!isS3Request) {
        // Only add Content-Type for non-S3 requests with data to reduce preflight triggers
        if (req.data && req.method !== 'get') {
          req.headers["Content-Type"] = "application/json";
        }
        
        const accessToken = localStorage.getItem("access_token");
        if (accessToken) {
          req.headers["Authorization"] = `Bearer ${accessToken}`;

          // Schedule proactive refresh if no timer is running
          if (!refreshTimerId && !isRefreshing) {
            scheduleProactiveRefresh();
          }

          // If token is within the buffer window, trigger refresh now
          // (handles background-tab timer throttling and edge cases)
          const exp = getTokenExpiry(accessToken);
          if (exp) {
            const secondsLeft = exp - Math.floor(Date.now() / 1000);
            if (secondsLeft > 0 && secondsLeft <= REFRESH_BUFFER && !isRefreshing) {
              doProactiveRefresh();
            }
          }
        }
      } else {
        // For S3 requests, remove any auth headers that might interfere
        delete req.headers.Authorization;
        delete req.headers.authorization;
        // S3 requests preserve their original Content-Type for signature calculation
        // Don't modify headers that are required for signature validation
      }
      
      return req;
    },
    err => {
      return Promise.reject(err);
    }
  );

  // Response interceptor
  axios.interceptors.response.use(
    res => {
      return res;
    },
    async err => {
      const originalConfig = err.config;
      
      // Handle authentication and token refresh logic first
      if (
        originalConfig.url !== `/api/login` &&
        !originalConfig.url?.includes('/api/token') && // Don't retry token refresh requests
        err.response &&
        (originalConfig.baseURL.includes(API_BASE_URL) ||
          originalConfig.baseURL.includes(localBaseUrl))
      ) {
        const { response } = err;

        // Handle non-expired JWT errors (should redirect to login immediately)
        if (JWT_ERROR_MESSAGES.includes(response.data?.errorMessage?.message)) {
          const currentPath = window.location.pathname;
          if (currentPath === "/oauth2" || currentPath === "/login") return Promise.reject(err);

          redirectToLogin();
          return Promise.reject(err);
        }

        // Handle 401 errors or expired JWT (should attempt token refresh)
        if ((response.status === 401 || JWT_EXPIRED_MESSAGES.includes(response.data?.errorMessage?.message)) && 
            !originalConfig._retry) {
          // Check if we're in cooldown period after recent refresh failure
          const now = Date.now();
          if (now - lastRefreshFailTime < REFRESH_COOLDOWN) {
            redirectToLogin();
            return Promise.reject(err);
          }

          // Check if we've exceeded maximum refresh attempts
          if (tokenRefreshAttempts >= MAX_REFRESH_ATTEMPTS) {
            localStorage.removeItem("access_token");
            lastRefreshFailTime = now;
            redirectToLogin();
            return Promise.reject(err);
          }

          // Mark as retry immediately to prevent any re-triggering
          originalConfig._retry = true;

          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject, config: originalConfig });
            });
          }
          isRefreshing = true;
          tokenRefreshAttempts++;

          try {
            await store.dispatch("moduleAuth/refreshToken");
            processQueue(null);
            scheduleProactiveRefresh();
            return axios(originalConfig);
          } catch (refreshError) {
            // Refresh failed - clear tokens and redirect
            localStorage.removeItem("access_token");
            lastRefreshFailTime = now;
            processQueue(refreshError);
            redirectToLogin();
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }

        // Phase 1C: 403 → the local permission context may be stale (permissions
        // changed mid-session). Refresh it once and retry; if it is still
        // forbidden, the user genuinely lacks access → return to /clinic.
        if (response.status === 403) {
          // Per-request opt-out: caller wants to handle the 403 itself
          // (e.g. inline "access denied" UI) instead of the permission
          // refresh + redirect-to-/clinic flow below.
          if (originalConfig.skipPermissionRedirect) {
            return Promise.reject(err);
          }
          // A 403 on the context endpoint itself can't be recovered by
          // refreshing it (would recurse) → treat as a genuine denial.
          if (originalConfig.url?.includes(PERMISSION_CONTEXT_URL)) {
            originalConfig.skipGlobalErrorHandler = true;
            redirectToClinic();
            return Promise.reject(err);
          }
          if (originalConfig._permRetry) {
            // Already refreshed + retried and still forbidden → genuine denial.
            originalConfig.skipGlobalErrorHandler = true;
            redirectToClinic();
            return Promise.reject(err);
          }
          originalConfig._permRetry = true;

          const clinicURL =
            store.state.moduleUser?.branchInfo?.clinic?.code ||
            store.state.moduleUser?.permissionContextClinicUrl;
          if (!clinicURL) {
            originalConfig.skipGlobalErrorHandler = true;
            redirectToClinic();
            return Promise.reject(err);
          }

          try {
            // Dedupe a burst of concurrent 403s into a single context refresh.
            if (!permRefreshPromise) {
              permRefreshPromise = store
                .dispatch("moduleUser/loadPermissionContext", { clinicURL })
                .finally(() => {
                  permRefreshPromise = null;
                });
            }
            await permRefreshPromise;
            return axios(originalConfig); // retry once with fresh context
          } catch (permErr) {
            originalConfig.skipGlobalErrorHandler = true;
            redirectToClinic();
            return Promise.reject(err);
          }
        }
      }

      // Centralized error handling for all non-auth errors
      // Skip error handling for certain scenarios to allow local handling
      const shouldSkipGlobalHandling = 
        originalConfig.skipGlobalErrorHandler || // Allow explicit opt-out
        originalConfig.url === `/api/login` || // Let login handle its own errors
        originalConfig.url?.includes('/oauth2/') || // Let OAuth2 handle its own errors
        err.response?.status === 401; // Auth errors are handled above

      if (!shouldSkipGlobalHandling) {
        // Check for duplicate errors to prevent spam
        if (!errorService.shouldSuppressError(err)) {
          // Handle error centrally with context
          const context = {
            url: originalConfig.url,
            method: originalConfig.method,
            isRetry: originalConfig._retry,
            requestId: originalConfig.requestId // if you use request IDs
          };
          
          errorService.handleError(err, context);
        }
      }

      return Promise.reject(err);
    }
  );

  // Wrap axios methods with request deduplication
  const originalGet = axios.get;
  const originalPost = axios.post;
  const originalPut = axios.put;
  const originalDelete = axios.delete;

  axios.get = function(url, config = {}) {
    return deduplicateRequest(() => originalGet.call(this, url, config), { 
      method: 'get', 
      url, 
      ...config 
    });
  };

  axios.post = function(url, data, config = {}) {
    return deduplicateRequest(() => originalPost.call(this, url, data, config), { 
      method: 'post', 
      url, 
      data, 
      ...config 
    });
  };

  axios.put = function(url, data, config = {}) {
    return deduplicateRequest(() => originalPut.call(this, url, data, config), { 
      method: 'put', 
      url, 
      data, 
      ...config 
    });
  };

  axios.delete = function(url, config = {}) {
    return deduplicateRequest(() => originalDelete.call(this, url, config), { 
      method: 'delete', 
      url, 
      ...config 
    });
  };
};

export default configureAxios;
export { axios, cancelProactiveRefresh };